import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

import { DEFAULT_REDIRECT_URL, DEFAULT_SELECTED_NODE, EXTERNAL_ORG_ID_CLAIM } from '@/constants';
import { toActionId, toAgentId } from '@/hooks/api';
import { createClientFromAuth } from '@/utils';

const ExcelIntegrationSearchSchema = z.object({
  code: z.optional(z.string()),
  code_challenge: z.optional(z.string()),
  session_state: z.optional(z.string()),
});

export const Route = createFileRoute('/_auth/integrations/excel')({
  validateSearch: zodValidator(ExcelIntegrationSearchSchema),
  beforeLoad: async ({ context: { auth, queryClient } }) => {
    // TODO: Find a way to get client from loader data from parent route
    const client = await createClientFromAuth(auth);

    const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
    const code = hashParams.get('code');

    const state = hashParams.get('state')?.split('|')?.[1];
    if (!state) {
      throw redirect({ to: DEFAULT_REDIRECT_URL });
    }

    const { actionId, agentId, scopesRequested } = JSON.parse(state);

    const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
    const codeVerifier = sessionStorage.getItem(`msal.${clientId}.code.verifier`);
    sessionStorage.setItem(`msal.${clientId}.code.value`, code!);
    const redirectUrl = import.meta.env.VITE_AZURE_REDIRECT_URI;

    if (code && codeVerifier && actionId) {
      await client.getAction(actionId).then((action) => {
        if (action?.connectionId) {
          client.deleteConnection(action.connectionId).catch(console.error);
        }
      });

      client
        .createConnection({
          config: { scopes: scopesRequested },
          setupParams: { redirectUrl, code, codeVerifier },
          type: 'microsoft-idp',
        })
        .then((conn) => {
          client.updateAction(actionId, { connectionId: conn.connectionId })?.then(async () => {
            const claim = await auth.getClaim(EXTERNAL_ORG_ID_CLAIM);
            const organizationId = claim?.value;
            queryClient.refetchQueries({ queryKey: [organizationId, 'actions', 'item', toActionId(actionId)] });
          });
        });
    }

    if (actionId) {
      sessionStorage.setItem(DEFAULT_SELECTED_NODE, actionId);
    }

    throw redirect({
      to: '/agents/$agentId/integration',
      params: { agentId },
    });
  },
});
